/** @file SplitPane: two panes with a draggable divider; one pane keeps a pixel size, the other fills. */
import { useRef, useState } from 'react';
import type { PointerEvent, ReactElement, ReactNode } from 'react';

/**
 * `direction` row = side by side, column = stacked; `fixed` says which pane keeps `initial` pixels (`start` = the
 * first, `end` = the second); dragging the divider resizes it within `min`..`max`.
 */
export type SplitPaneProps = {
  direction: 'row' | 'column'; fixed: 'start' | 'end'; initial: number; min?: number; max?: number;
  label?: string; children: [ReactNode, ReactNode];
  /** Hides the fixed pane and the divider (the other pane fills); both stay mounted so nothing restarts. */
  collapsed?: boolean;
};

/** The container's classes: column layout, and `p3-split-collapsed` (tokens.css hides the fixed pane and the divider). */
const splitClass = (direction: SplitPaneProps['direction'], collapsed: boolean | undefined): string =>
  ['p3-split', direction === 'column' ? 'p3-split-column' : '', collapsed === true ? 'p3-split-collapsed' : ''].filter((c) => c !== '').join(' ');

/** Resizes with pointer capture on the divider; no window listeners. */
export function SplitPane({ direction, fixed, initial, min = 120, max = 900, label, collapsed, children }: SplitPaneProps): ReactElement {
  const [size, setSize] = useState(initial);
  const drag = useRef<{ start: number; size: number } | null>(null);
  const axis = (e: PointerEvent): number => (direction === 'row' ? e.clientX : e.clientY);
  const onDown = (e: PointerEvent<HTMLDivElement>): void => {
    drag.current = { start: axis(e), size };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: PointerEvent): void => {
    const d = drag.current;
    if (d === null) return;
    const delta = (axis(e) - d.start) * (fixed === 'start' ? 1 : -1);
    setSize(Math.min(max, Math.max(min, d.size + delta)));
  };
  const onUp = (): void => { drag.current = null; };
  const basis = { flex: `0 0 ${String(size)}px` };
  const [first, second] = children;
  return (
    <div className={splitClass(direction, collapsed)} aria-label={label}>
      <div className={fixed === 'start' ? 'p3-split-pane' : 'p3-split-pane p3-split-fill'} style={fixed === 'start' ? basis : undefined}>{first}</div>
      <div className="p3-split-divider" role="separator" aria-orientation={direction === 'row' ? 'vertical' : 'horizontal'}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} />
      <div className={fixed === 'end' ? 'p3-split-pane' : 'p3-split-pane p3-split-fill'} style={fixed === 'end' ? basis : undefined}>{second}</div>
    </div>
  );
}
